// src/context/CartContext.jsx - GELİŞTİRİLMİŞ VERSİYON
import React, { createContext, useContext, useReducer, useEffect, useState, useMemo } from 'react';
import { getFreeShippingCampaign } from '../services/discountService';

const CartContext = createContext();

// Action types
const ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  LOAD_CART: 'LOAD_CART',
  SET_FREE_SHIPPING_CAMPAIGN: 'SET_FREE_SHIPPING_CAMPAIGN'
};

// ✅ LocalStorage Key - Kullanıcıya özel
const getCartStorageKey = () => {
  const userId = localStorage.getItem('userId');
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  
  if (isLoggedIn && userId) {
    return `trona_cart_${userId}`; // Üye: trona_cart_user123
  }
  return 'trona_cart_guest'; // Misafir: trona_cart_guest
};

// ✅ Ürün validasyonu
const validateCartItem = (item) => {
  if (!item || typeof item !== 'object') return null;
  if (!item.id || !item.name) return null;
  if (typeof item.price !== 'number' && isNaN(parseFloat(item.price))) return null;
  
  return {
    id: item.id,
    name: item.name,
    price: Number(item.price) || 0,
    currentPrice: item.currentPrice ? Number(item.currentPrice) : Number(item.price) || 0,
    shippingFee: Number(item.shippingFee) || 0,
    quantity: Math.max(1, Number(item.quantity) || 1),
    image: item.image || '',
    category: item.category || '',
    stock: Number(item.stock) || 0,
    // ✅ Timestamp ekle - eski ürünleri temizlemek için
    addedAt: item.addedAt || Date.now()
  };
};

// Reducer
function cartReducer(state, action) {
  switch (action.type) {
    case ACTIONS.ADD_ITEM: {
      const validatedItem = validateCartItem(action.payload);
      if (!validatedItem) {
        console.warn('⚠️ Geçersiz ürün ekleme denemesi:', action.payload);
        return state;
      }

      const existingIndex = state.items.findIndex(item => item.id === validatedItem.id);
      
      if (existingIndex > -1) {
        const updatedItems = [...state.items];
        const existingItem = updatedItems[existingIndex];
        
        updatedItems[existingIndex] = {
          ...validatedItem,
          quantity: existingItem.quantity + validatedItem.quantity,
          addedAt: existingItem.addedAt // Eski timestamp'i koru
        };
        
        console.log('🔄 Sepette güncellendi:', updatedItems[existingIndex].name);
        return { ...state, items: updatedItems };
      }
      
      console.log('➕ Sepete eklendi:', validatedItem.name);
      return {
        ...state,
        items: [...state.items, { ...validatedItem, addedAt: Date.now() }]
      };
    }
    
    case ACTIONS.REMOVE_ITEM:
      console.log('🗑️ Sepetten çıkarıldı:', action.payload);
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };
    
    case ACTIONS.UPDATE_QUANTITY: {
      const quantity = Math.max(0, Number(action.payload.quantity) || 0);
      
      if (quantity <= 0) {
        console.log('🗑️ Miktar 0, ürün kaldırılıyor:', action.payload.id);
        return {
          ...state,
          items: state.items.filter(item => item.id !== action.payload.id)
        };
      }
      
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity }
            : item
        )
      };
    }
    
    case ACTIONS.CLEAR_CART:
      console.log('🧹 Sepet tamamen temizlendi');
      return { ...state, items: [] };
    
    case ACTIONS.LOAD_CART: {
      const validatedItems = (action.payload || [])
        .map(validateCartItem)
        .filter(Boolean);
      
      console.log('📥 Sepet yüklendi:', validatedItems.length, 'ürün');
      return { ...state, items: validatedItems };
    }
    
    case ACTIONS.SET_FREE_SHIPPING_CAMPAIGN:
      return { ...state, freeShippingCampaign: action.payload };
    
    default:
      return state;
  }
}

// Provider Component
export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, { 
    items: [],
    freeShippingCampaign: null
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // ✅ Login/Logout dinle - Sepeti yenile
  useEffect(() => {
    const handleAuthChange = () => {
      console.log('🔐 Auth değişikliği tespit edildi, sepet yenileniyor...');
      loadCartFromStorage();
    };

    window.addEventListener('authChange', handleAuthChange);
    return () => window.removeEventListener('authChange', handleAuthChange);
  }, []);

  // ✅ İlk yükleme - localStorage'dan sepeti al
  const loadCartFromStorage = () => {
    try {
      const storageKey = getCartStorageKey();
      console.log('🔄 Sepet yükleniyor - Key:', storageKey);
      
      const savedCart = localStorage.getItem(storageKey);
      
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        
        // ✅ 30 günden eski ürünleri temizle
        const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
        const recentItems = parsedCart.filter(item => {
          const addedAt = item.addedAt || 0;
          return addedAt > thirtyDaysAgo;
        });

        if (recentItems.length !== parsedCart.length) {
          console.log(`🧹 ${parsedCart.length - recentItems.length} eski ürün temizlendi`);
        }
        
        const validatedCart = recentItems
          .map(validateCartItem)
          .filter(Boolean);
        
        if (validatedCart.length > 0) {
          console.log('💾 LocalStorage\'dan yüklendi:', validatedCart.length, 'ürün');
          dispatch({ type: ACTIONS.LOAD_CART, payload: validatedCart });
        } else {
          console.log('💾 Geçerli ürün yok, temizleniyor');
          localStorage.removeItem(storageKey);
        }
      } else {
        console.log('💾 LocalStorage\'da sepet yok');
      }
    } catch (error) {
      console.error('❌ LocalStorage yükleme hatası:', error);
      const storageKey = getCartStorageKey();
      localStorage.removeItem(storageKey);
    }
  };

  // ✅ İlk yükleme
  useEffect(() => {
    console.log('🚀 CartProvider başlatılıyor...');
    loadCartFromStorage();
    setIsInitialized(true);
  }, []);

  // ✅ Ücretsiz kargo kampanyasını yükle
  useEffect(() => {
    const loadFreeShippingCampaign = async () => {
      try {
        const result = await getFreeShippingCampaign();
        if (result.success && result.campaign) {
          dispatch({ 
            type: ACTIONS.SET_FREE_SHIPPING_CAMPAIGN, 
            payload: result.campaign 
          });
        }
      } catch (error) {
        console.error('❌ Ücretsiz kargo kampanyası yükleme hatası:', error);
      }
    };

    loadFreeShippingCampaign();
    
    // Her 5 dakikada bir kontrol et
    const interval = setInterval(loadFreeShippingCampaign, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // ✅ Sepet değiştiğinde localStorage'a kaydet
  useEffect(() => {
    if (!isInitialized) return;
    
    const storageKey = getCartStorageKey();
    
    try {
      if (state.items.length === 0) {
        localStorage.removeItem(storageKey);
        console.log('💾 Sepet boş, localStorage temizlendi');
      } else {
        const itemsToSave = state.items.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          currentPrice: item.currentPrice,
          shippingFee: item.shippingFee,
          quantity: item.quantity,
          image: item.image,
          category: item.category,
          stock: item.stock,
          addedAt: item.addedAt
        }));
        
        localStorage.setItem(storageKey, JSON.stringify(itemsToSave));
        console.log('💾 Sepet kaydedildi:', itemsToSave.length, 'ürün');
      }
    } catch (error) {
      console.error('❌ LocalStorage kaydetme hatası:', error);
    }
  }, [state.items, isInitialized]);

  // ✅ Hesaplamalar - useMemo ile optimize edildi
  const cartCount = useMemo(() => 
    state.items.reduce((sum, item) => sum + item.quantity, 0),
    [state.items]
  );
  
  const cartTotal = useMemo(() => 
    state.items.reduce((sum, item) => sum + (item.price * item.quantity), 0),
    [state.items]
  );

  const cartTotalWithDiscount = useMemo(() => 
    state.items.reduce((sum, item) => 
      sum + ((item.currentPrice || item.price) * item.quantity), 0
    ),
    [state.items]
  );

  // ✅ Dinamik kargo ücreti
  const shippingCost = useMemo(() => {
    const freeShippingThreshold = state.freeShippingCampaign?.freeShippingThreshold || 0;
    const hasFreeShippingCampaign = state.freeShippingCampaign && freeShippingThreshold > 0;

    if (hasFreeShippingCampaign && cartTotalWithDiscount >= freeShippingThreshold) {
      return 0;
    }

    return state.items.reduce((sum, item) => 
      sum + ((item.shippingFee || 0) * item.quantity), 0
    );
  }, [state.items, state.freeShippingCampaign, cartTotalWithDiscount]);

  // ✅ Ücretsiz kargo için kalan tutar
  const remainingForFreeShipping = useMemo(() => {
    const freeShippingThreshold = state.freeShippingCampaign?.freeShippingThreshold || 0;
    const hasFreeShippingCampaign = state.freeShippingCampaign && freeShippingThreshold > 0;

    if (!hasFreeShippingCampaign) return null;

    return Math.max(0, freeShippingThreshold - cartTotalWithDiscount);
  }, [state.freeShippingCampaign, cartTotalWithDiscount]);

  const finalTotal = useMemo(() => 
    cartTotalWithDiscount + shippingCost,
    [cartTotalWithDiscount, shippingCost]
  );

  // ✅ Actions
  const addToCart = (product, quantity = 1) => {
    const normalizedProduct = {
      ...product,
      price: Number(product.price) || 0,
      currentPrice: product.currentPrice ? Number(product.currentPrice) : Number(product.price) || 0,
      shippingFee: Number(product.shippingFee) || 0,
      quantity: Number(quantity) || 1
    };
    
    dispatch({ type: ACTIONS.ADD_ITEM, payload: normalizedProduct });
  };

  const removeFromCart = (productId) => {
    dispatch({ type: ACTIONS.REMOVE_ITEM, payload: productId });
  };

  const updateQuantity = (productId, quantity) => {
    dispatch({ type: ACTIONS.UPDATE_QUANTITY, payload: { id: productId, quantity: Number(quantity) || 0 } });
  };

  const clearCart = () => {
    const storageKey = getCartStorageKey();
    localStorage.removeItem(storageKey);
    dispatch({ type: ACTIONS.CLEAR_CART });
  };

  const isInCart = (productId) => {
    return state.items.some(item => item.id === productId);
  };

  const getItemQuantity = (productId) => {
    const item = state.items.find(item => item.id === productId);
    return item ? item.quantity : 0;
  };

  // ✅ Sepeti yenile
  const refreshCart = async () => {
    console.log('🔄 refreshCart çağrıldı');
    
    try {
      loadCartFromStorage();

      const result = await getFreeShippingCampaign();
      if (result.success && result.campaign) {
        dispatch({ 
          type: ACTIONS.SET_FREE_SHIPPING_CAMPAIGN, 
          payload: result.campaign 
        });
      }
      
      return { success: true };
    } catch (error) {
      console.error('❌ refreshCart hatası:', error);
      return { success: false, error: error.message };
    }
  };

  return (
    <CartContext.Provider value={{
      items: state.items,
      cartCount,
      cartTotal,
      cartTotalWithDiscount,
      shippingCost,
      finalTotal,
      freeShippingCampaign: state.freeShippingCampaign,
      remainingForFreeShipping,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      isInCart,
      getItemQuantity,
      refreshCart
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}